import { COLOR, COMPANY, PCT, SHEET, INR_COMPACT } from '../tokens';
import {
  applyPortraitPrint,
  clipSheetToColumns,
  ensureMinColumnWidths,
  estimateWrappedRowHeight,
  formatGeneratedAt,
  formatPeriod,
  setWidths,
  writeExecutiveHeader,
  writePremiumMetaBand,
  writeSectionGap,
  writeSectionTitle,
  styleTableHeader,
} from '../layout';
import {
  addDataTable,
  applyInrFormatToTableColumn,
  applyTableBodyFont,
  currencyColumn,
  labelTotalColumn,
  sumColumn,
  textColumn,
} from '../tableFactory';

const COLS = 6;
const WIDTHS = [30, 12, 22, 22, 24, 12];
const CURRENCY_COLS = [3, 4, 5];

export function buildGstAnalysisSheet(workbook, ctx, tableName) {
  const { analytics, meta } = ctx;

  const sheet = workbook.addWorksheet(SHEET.GST, {
    properties: { tabColor: { argb: COLOR.warn }, defaultRowHeight: 20 },
  });
  setWidths(sheet, WIDTHS);
  clipSheetToColumns(sheet, COLS);

  let row = 1;
  row = writeExecutiveHeader(
    sheet,
    row,
    COMPANY.name,
    'GST Analysis',
    COLS,
  );

  writePremiumMetaBand(sheet, row, COLS,
    [
      { text: `Period: ${formatPeriod(meta.from, meta.to)}`, from: 1, to: 3 },
      { text: `Generated: ${formatGeneratedAt()}`, from: 4, to: COLS },
    ],
    [
      { text: `Report ID: ${meta.reportId || '—'}`, from: 1, to: 3 },
      { text: 'Tax rate: 18% GST', from: 4, to: COLS },
    ],
  );
  row += 2;
  writeSectionGap(sheet, row, COLS);
  row += 1;
  writeSectionTitle(sheet, row, 'GST Analysis by Channel', COLS, { center: true });
  row += 1;

  const tableRows = analytics.gstByChannel.map((item) => {
    const rate = item.taxableAmount ? item.gstAmount / item.taxableAmount : 0;
    return [item.label, item.count, item.taxableAmount, item.gstAmount, item.totalInclGst, rate];
  });

  const table = addDataTable(sheet, {
    startRow: row,
    columns: [
      labelTotalColumn('Channel', { filter: false }),
      sumColumn('Transactions', { filter: false, total: true }),
      currencyColumn('Taxable', { filter: false, total: true }),
      currencyColumn('GST Amount', { filter: false, total: true }),
      currencyColumn('Total Incl GST', { filter: false, total: true }),
      textColumn('Eff Rate', { filter: false }),
    ],
    rows: tableRows.length ? tableRows : [['No data', 0, 0, 0, 0, 0]],
    totalsRow: tableRows.length > 0,
  });

  CURRENCY_COLS.forEach((ci) => {
    applyInrFormatToTableColumn(
      sheet,
      ci,
      table.dataStartRow,
      table.totalsRow || table.dataEndRow,
      INR_COMPACT,
    );
  });
  applyTableBodyFont(sheet, table.dataStartRow, table.dataEndRow, 1, COLS);
  styleTableHeader(sheet, table.headerRow, [
    { col: 1, align: 'left' },
    { col: 2, align: 'center' },
    { col: 3, align: 'right' },
    { col: 4, align: 'right' },
    { col: 5, align: 'right' },
    { col: 6, align: 'center' },
  ]);

  analytics.gstByChannel.forEach((item, i) => {
    const rNum = table.dataStartRow + i;
    const rowHeight = estimateWrappedRowHeight(item.label, WIDTHS[0], { minHeight: 24 });
    sheet.getRow(rNum).height = rowHeight;
    sheet.getCell(rNum, 1).alignment = {
      horizontal: 'left',
      vertical: 'middle',
      wrapText: true,
      indent: 1,
      shrinkToFit: false,
    };
    sheet.getCell(rNum, 2).alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getCell(rNum, 6).numFmt = PCT;
    sheet.getCell(rNum, 6).alignment = { horizontal: 'center', vertical: 'middle' };
    CURRENCY_COLS.forEach((ci) => {
      sheet.getCell(rNum, ci).alignment = { horizontal: 'right', vertical: 'middle', shrinkToFit: false };
    });
  });

  if (table.totalsRow) {
    sheet.getCell(table.totalsRow, 1).alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: false };
    sheet.getRow(table.totalsRow).height = 28;
  }

  setWidths(sheet, WIDTHS);
  ensureMinColumnWidths(sheet, WIDTHS);
  clipSheetToColumns(sheet, COLS);
  applyPortraitPrint(sheet, {
    colCount: COLS,
    lastRow: table.totalsRow || table.dataEndRow,
    printTitlesRow: '1:4',
    fitOnePage: false,
  });
  ensureMinColumnWidths(sheet, WIDTHS);

  return sheet;
}
